<template>
  <div class="p-6 space-y-6 max-w-full">
    <!-- Page header -->
    <div class="flex items-center justify-between flex-wrap gap-4 border-b border-border/40 pb-4 mb-6">
      <div class="space-y-1">
        <h2 class="text-2xl font-black tracking-tight text-foreground uppercase italic">Budget <span class="text-indigo-500">Prévisionnel</span></h2>
        <p class="text-xs text-muted-foreground font-medium">
          Planification prévisionnelle — Compte Pro & Perso
        </p>
      </div>

      <!-- Actions (Year + History) -->
      <div class="flex items-center gap-4">
        <!-- History button -->
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          @click="showHistoryModal = true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
          Historique
        </button>

        <!-- Year selector -->
        <div class="flex items-center gap-2">
          <label for="budget-year-select" class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Année</label>
        
        <div class="flex items-center gap-1">
          <button 
            type="button"
            class="p-1.5 rounded-lg bg-muted border border-border text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-muted disabled:hover:text-foreground disabled:cursor-not-allowed transition flex items-center justify-center"
            :disabled="!canGoPrev"
            @click="goPrev"
            aria-label="Année précédente"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>

          <select
            id="budget-year-select"
            v-model.number="selectedYear"
            class="px-3 py-1.5 rounded-lg bg-muted border border-border text-sm font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
            @change="onYearChange"
            aria-label="Sélectionner l'année budgétaire"
          >
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>

          <button 
            type="button"
            class="p-1.5 rounded-lg bg-muted border border-border text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:hover:bg-muted disabled:hover:text-foreground disabled:cursor-not-allowed transition flex items-center justify-center"
            :disabled="!canGoNext"
            @click="goNext"
            aria-label="Année suivante"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-20">
      <div class="flex flex-col items-center gap-4 text-muted-foreground">
        <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p class="text-sm font-medium">Chargement du budget…</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="rounded-xl bg-red-500/10 border border-red-500/20 p-6 text-center">
      <p class="text-sm text-red-400 font-medium">{{ error }}</p>
      <button class="mt-3 text-xs text-red-400 underline" @click="loadYear">Réessayer</button>
    </div>

    <!-- Content -->
    <template v-else-if="budgetData">
      <!-- Read Only Banner -->
      <div v-if="isReadOnlyMode && currentSnapshot" class="flex items-center justify-between p-4 mb-6 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500">
        <div class="flex items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h10"/><path d="M9 4v16"/><path d="M17 2l5 5-5 5"/><path d="M17 22l5-5-5-5"/></svg>
          <div>
            <p class="text-sm font-bold">MODE LECTURE SEULE - VERSION : {{ currentSnapshot.name || 'Sauvegarde Automatique' }}</p>
            <p class="text-xs opacity-80">Créée le {{ new Date(currentSnapshot.updatedAt || currentSnapshot.createdAt).toLocaleString() }}</p>
          </div>
        </div>
        <button
          class="px-4 py-2 rounded-lg bg-amber-500 text-amber-950 font-bold text-sm hover:bg-amber-400 transition"
          @click="exitReadOnly"
        >
          Retour au budget actuel
        </button>
      </div>

      <!-- Annual Summary -->
      <BudgetAnnualSummary
        :year="selectedYear"
        :personal-data="budgetData.personal"
        :professional-data="budgetData.professional"
        :resolved-personal="resolvedPersonal"
        :resolved-professional="resolvedProfessional"
        :previous-personal-carry="previousPersonalCarry"
        :previous-professional-carry="previousProfessionalCarry"
        :previous-personal-net="previousPersonalNet"
        :previous-professional-net="previousProfessionalNet"
        class="mb-6"
      />

      <!-- Single Budget table, reactive to activeTab switcher inside its header -->
      <BudgetTable
        :account="activeTab"
        :data="activeTab === 'personal' ? budgetData.personal : budgetData.professional"
        :resolved-values="activeTab === 'personal' ? resolvedPersonal : resolvedProfessional"
        :year="selectedYear"
        :previous-year-carry="activeTab === 'personal' ? previousPersonalCarry : previousProfessionalCarry"
        @change-account="(newAccount) => activeTab = newAccount"
        @add-category="openModal(null, activeTab, null)"
        @add-category-of-type="(type) => openModal(null, activeTab, type)"
        @edit-category="(cat) => openModal(cat, null, null)"
      />
    </template>

    <!-- Empty state (no data at all) -->
    <div v-else class="text-center py-16 text-muted-foreground">
      <div class="text-4xl mb-4">📊</div>
      <p class="text-sm font-medium">Aucune donnée budgétaire pour {{ selectedYear }}</p>
      <p class="text-xs mt-1">Commencez par ajouter des catégories</p>
    </div>

    <!-- Category modal -->
    <BudgetCategoryModal
      :is-open="modalOpen"
      :edit-category="editingCategory"
      :prefilled-account-type="modalAccountType"
      :prefilled-type="modalType"
      :all-categories="allCategories"
      @close="closeModal"
      @saved="onCategorySaved"
    />

    <!-- History Modal -->
    <BudgetHistoryModal
      :is-open="showHistoryModal"
      @close="showHistoryModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { BudgetCategoryData } from '../../../composables/useBudget'
import { useBudget } from '../../../composables/useBudget'
import { useBudgetFormula } from '../../../composables/useBudgetFormula'
import BudgetAnnualSummary from './BudgetAnnualSummary.vue'
import BudgetTable from './BudgetTable.vue'
import BudgetCategoryModal from './BudgetCategoryModal.vue'
import BudgetHistoryModal from './BudgetHistoryModal.vue'
import { calculateIncomeTaxSync } from '../../../composables/useUrssafApi'

// ── Composables ───────────────────────────────────────────────────────────────

const { currentYear, budgetData, budgetDataByYear, taxStrategies, loading, error, fetchYear, fetchSnapshots, isReadOnlyMode, currentSnapshot, exitReadOnlyMode } = useBudget()
const { resolveAllYears } = useBudgetFormula()

// ── State ─────────────────────────────────────────────────────────────────────

const selectedYear = ref<number>(currentYear.value)
const activeTab         = ref<'personal' | 'professional'>('personal')

const modalOpen         = ref(false)
const editingCategory   = ref<BudgetCategoryData | null>(null)
const modalAccountType  = ref<'personal' | 'professional' | null>(null)
const modalType         = ref<'income' | 'expense' | null>(null)

const showHistoryModal  = ref(false)

// ── Computed ──────────────────────────────────────────────────────────────────

const yearOptions = computed(() => {
  const base = new Date().getFullYear()
  return [base - 1, base, base + 1, base + 2, base + 3]
})

/** All categories across both accounts, used by the modal for formula references */
const allCategories = computed<BudgetCategoryData[]>(() => {
  if (!budgetData.value) return []
  return [
    ...budgetData.value.personal.categories,
    ...budgetData.value.professional.categories,
  ]
})

/**
 * resolveAllYears computes sequential formulas across all loaded years.
 * We extract the resolved data for the currently selected year.
 */
const resolvedAll = computed(() => {
  return resolveAllYears(budgetDataByYear.value)
})

const resolvedPersonal     = computed(() => resolvedAll.value[selectedYear.value] ?? {})
const resolvedProfessional = computed(() => resolvedAll.value[selectedYear.value] ?? {})

function getYearEndBalance(accountType: 'personal' | 'professional', year: number): number | null {
  const data = budgetDataByYear.value[year]?.[accountType]
  const resolved = resolvedAll.value[year]
  if (!data || !resolved) return null

  const monthlyTotals = useBudgetFormula().computeMonthlyTotals(data.categories, resolved)
  const prevYearEnd = getYearEndBalance(accountType, year - 1)
  
  const balances = useBudgetFormula().computeOpeningBalances(data.openingBalances, monthlyTotals, prevYearEnd)
  const decOpening = balances[12].amount
  if (decOpening === null) return null
  
  return decOpening + (monthlyTotals[12]?.balance ?? 0)
}

const previousPersonalCarry = computed(() => getYearEndBalance('personal', selectedYear.value - 1))
const previousProfessionalCarry = computed(() => getYearEndBalance('professional', selectedYear.value - 1))

function getYearNetBalance(accountType: 'personal' | 'professional', year: number): number | null {
  const data = budgetDataByYear.value[year]?.[accountType]
  const resolved = resolvedAll.value[year]
  if (!data || !resolved) return null

  const endOfYearBalance = getYearEndBalance(accountType, year)
  if (endOfYearBalance === null) return null

  let totalOtherDebts = 0
  for (const cat of data.categories) {
    if (cat.type === 'parameter' && cat.isDebt) {
      totalOtherDebts += (resolved[cat.id]?.[12] ?? 0)
    }
  }

  if (accountType === 'professional') {
    let totalTns = 0
    let totalPaid = 0
    for (const cat of data.categories) {
      if (cat.type === 'expense' && cat.isTnsRemuneration) {
        for (let m = 1; m <= 12; m++) {
          totalTns += (resolved[cat.id]?.[m] ?? 0)
        }
      }
      if (cat.type === 'expense' && cat.isUrssafPayment) {
        for (let m = 1; m <= 12; m++) {
          totalPaid += (resolved[cat.id]?.[m] ?? 0)
        }
      }
    }
    const strategy = taxStrategies.value[year]
    const urssafRate = strategy?.urssafRate ?? 0.45
    const estimation = totalTns * urssafRate
    return endOfYearBalance - Math.max(0, estimation - totalPaid) - totalOtherDebts
  } else {
    let totalTaxable = 0
    let totalPaid = 0
    for (const cat of data.categories) {
      if (cat.type === 'income' && cat.isTaxable) {
        for (let m = 1; m <= 12; m++) {
          totalTaxable += (resolved[cat.id]?.[m] ?? 0)
        }
      }
      if (cat.type === 'expense' && cat.isTaxPayment) {
        for (let m = 1; m <= 12; m++) {
          totalPaid += (resolved[cat.id]?.[m] ?? 0)
        }
      }
    }
    const estimatedTax = calculateIncomeTaxSync(totalTaxable, year, taxStrategies.value)
    return endOfYearBalance - Math.max(0, estimatedTax - totalPaid) - totalOtherDebts
  }
}

const previousPersonalNet = computed(() => getYearNetBalance('personal', selectedYear.value - 1))
const previousProfessionalNet = computed(() => getYearNetBalance('professional', selectedYear.value - 1))

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => loadYear())

// ── Actions ───────────────────────────────────────────────────────────────────

async function loadYear(): Promise<void> {
  await fetchYear(selectedYear.value)
  await fetchSnapshots()
}

function onYearChange(): void {
  loadYear()
}

const canGoPrev = computed(() => selectedYear.value > yearOptions.value[0])
const canGoNext = computed(() => selectedYear.value < yearOptions.value[yearOptions.value.length - 1])

function goPrev(): void {
  if (canGoPrev.value) {
    selectedYear.value--
    onYearChange()
  }
}

function goNext(): void {
  if (canGoNext.value) {
    selectedYear.value++
    onYearChange()
  }
}

function openModal(
  cat: BudgetCategoryData | null,
  accountType: 'personal' | 'professional' | null,
  type: 'income' | 'expense' | null,
): void {
  editingCategory.value  = cat
  modalAccountType.value = accountType
  modalType.value        = type
  modalOpen.value        = true
}

function closeModal(): void {
  modalOpen.value        = false
  editingCategory.value  = null
  modalAccountType.value = null
  modalType.value        = null
}

function onCategorySaved(_cat: BudgetCategoryData): void {
  // Data is already updated optimistically in useBudget — nothing to do here
}

async function exitReadOnly() {
  await exitReadOnlyMode()
  await fetchSnapshots()
}
</script>
